import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';

const FormSection = styled.section`
  padding: 100px 0;
  background-color: #f9f9f9;
  
  .section-title {
    text-align: center;
    margin-bottom: 50px;
    
    h2 {
      font-size: 2.5rem;
      font-weight: 700;
      color: #333;
      margin-bottom: 20px;
      
      span {
        color: #2e7d32;
      }
    }
    
    p {
      color: #666;
      font-size: 1.1rem;
      max-width: 700px;
      margin: 0 auto;
    }
  }
  
  .contact-form {
    background-color: #fff;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    padding: 40px;
    
    .form-label {
      font-weight: 600;
      color: #444;
      margin-bottom: 8px;
    }
    
    .form-control {
      height: 50px;
      border-radius: 8px;
      border: 1px solid #ddd;
      padding: 10px 15px;
      font-size: 1rem;
      transition: all 0.3s ease;
      
      &:focus {
        border-color: #2e7d32;
        box-shadow: 0 0 0 0.2rem rgba(46, 125, 50, 0.25);
      }
      
      &::placeholder {
        color: #aaa;
      }
    }
    
    textarea.form-control {
      height: auto;
      min-height: 150px;
      resize: vertical;
    }
    
    .form-select {
      height: 50px;
      border-radius: 8px;
      border: 1px solid #ddd;
      padding: 10px 15px;
      font-size: 1rem;
      transition: all 0.3s ease;
      
      &:focus {
        border-color: #2e7d32;
        box-shadow: 0 0 0 0.2rem rgba(46, 125, 50, 0.25);
      }
    }
    
    .btn-submit {
      height: 50px;
      font-weight: 600;
      font-size: 1.1rem;
      border-radius: 8px;
      background-color: #2e7d32;
      border-color: #2e7d32;
      padding: 0 30px;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      
      svg {
        margin-left: 10px;
      }
      
      &:hover, &:focus {
        background-color: #1b5e20;
        border-color: #1b5e20;
        transform: translateY(-2px);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
      }
    }
  }
  
  .form-privacy {
    display: flex;
    align-items: center;
    margin-top: 20px;
    color: #777;
    font-size: 0.9rem;
    
    svg {
      margin-right: 8px;
      color: #2e7d32;
    }
  }
  
  .contact-info {
    height: 100%;
    background: linear-gradient(to right bottom, #2e7d32, #1b5e20);
    border-radius: 15px;
    overflow: hidden;
    position: relative;
    padding: 40px;
    color: #fff;
    
    &:before {
      content: '';
      position: absolute;
      top: -50px;
      right: -50px;
      width: 150px;
      height: 150px;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.1);
    }
    
    &:after {
      content: '';
      position: absolute;
      bottom: -80px;
      left: -80px;
      width: 200px;
      height: 200px;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.1);
    }
    
    .contact-heading {
      font-size: 1.8rem;
      font-weight: 700;
      margin-bottom: 30px;
      position: relative;
      z-index: 1;
      
      &:after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -10px;
        width: 50px;
        height: 3px;
        background-color: #ff9800;
      }
    }
    
    .contact-details {
      position: relative;
      z-index: 1;
      
      p {
        margin-bottom: 25px;
        font-size: 1.1rem;
        line-height: 1.7;
      }
    }
    
    .highlight {
      color: #ff9800;
      font-weight: 700;
    }
    
    .contact-list {
      list-style: none;
      padding-left: 0;
      margin-top: 40px;
      
      li {
        display: flex;
        align-items: center;
        margin-bottom: 20px;
        font-size: 1rem;
        
        svg {
          margin-right: 15px;
          width: 20px;
          color: #ff9800;
        }
      }
    }
  }
`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    landLocation: '',
    propertyType: '',
    acreage: '',
    message: ''
  }); // Note: kept acreage field name for backend compatibility
  const [validated, setValidated] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = (e) => {
    const form = e.currentTarget;
    
    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      // Let the form submit to FormSubmit.co
      // The page will be redirected back after submission
      // We don't prevent default here to allow the form to submit
      
      // Show success message will be handled in useEffect when redirected back
      // with a query parameter
    }
    
    setValidated(true);
  };
  
  // Check for success parameter in URL when component mounts
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('submitted') === 'true') {
      // Clear the URL parameter without refreshing the page
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Show success message
      setShowSuccess(true);
      
      // Reset form data
      setFormData({
        name: '',
        email: '',
        phone: '',
        landLocation: '',
        propertyType: '',
        acreage: '',
        message: ''
      });
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    }
  }, []);

  return (
    <FormSection id="contact-form">
      <Container>
        <div className="section-title">
          <h2>Get Your <span>Cash Offer</span></h2>
          <p>
            Fill out the form below with details about your land property, and we'll get back to you 
            with a fair cash offer within 24-48 hours.
          </p>
        </div>
        
        <Row className="g-4">
          <Col lg={7}>
            <div className="contact-form">
              {showSuccess && (
                <Alert variant="success" onClose={() => setShowSuccess(false)} dismissible>
                  Thank you for your submission! We'll contact you with an offer within 24-48 hours.
                </Alert>
              )}
              <Form 
                noValidate 
                validated={validated} 
                onSubmit={handleSubmit}
                action="https://formsubmit.co/connect@beringsolutions.com" 
                method="POST"
              >
                {/* FormSubmit.co configuration fields */}
                <input type="hidden" name="_subject" value="New Land Offer Request - Bering Solutions" />
                <input type="hidden" name="_template" value="table" />
                <input type="hidden" name="_next" value={window.location.href + '?submitted=true'} />
                <input type="hidden" name="_captcha" value="true" />
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group controlId="name">
                      <Form.Label>Your Name</Form.Label>
                      <Form.Control
                        required
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide your name.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="email">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        required
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="example@email.com"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a valid email.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group controlId="phone">
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control
                        required
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(123) 456-7890"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide your phone number.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="landLocation">
                      <Form.Label>Property Location</Form.Label>
                      <Form.Control
                        required
                        type="text"
                        name="landLocation"
                        value={formData.landLocation}
                        onChange={handleChange}
                        placeholder="City, State"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide the property location.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group controlId="propertyType">
                      <Form.Label>Property Type</Form.Label>
                      <Form.Select
                        required
                        name="propertyType"
                        value={formData.propertyType}
                        onChange={handleChange}
                      >
                        <option value="">Select Property Type</option>
                        <option value="Vacant Land">Vacant Land</option>
                        <option value="Ranch">Ranch Land</option>
                        <option value="Farm">Farm Land</option>
                        <option value="Mountain">Mountain Property</option>
                        <option value="Waterfront">Waterfront</option>
                        <option value="Recreational">Recreational Land</option>
                        <option value="Commercial">Commercial Land</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        Please select a property type.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="acreage">
                      <Form.Label>Land ID or APN Number</Form.Label>
                      <Form.Control
                        required
                        type="text"
                        name="acreage"
                        value={formData.acreage}
                        onChange={handleChange}
                        placeholder="e.g. 123456789"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide the Land ID or APN Number.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-4" controlId="message">
                  <Form.Label>Additional Information</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Please share any additional details about your property that would help us make an accurate offer."
                  />
                </Form.Group>
                
                <Button type="submit" className="btn-submit">
                  Submit Request <FontAwesomeIcon icon={faPaperPlane} />
                </Button>
                
                <div className="form-privacy">
                  <FontAwesomeIcon icon={faShieldAlt} />
                  <span>Your information is kept confidential and secure.</span>
                </div>
              </Form>
            </div>
          </Col>
          <Col lg={5}>
            <div className="contact-info h-100 d-flex flex-column justify-content-center">
              <h3 className="contact-heading">Why Choose Us?</h3>
              <div className="contact-details">
                <p>
                  We specialize in purchasing land directly from owners, offering a 
                  <span className="highlight"> fast, simple, and stress-free </span> 
                  selling experience.
                </p>
                <p>
                  Our process eliminates the headaches of traditional real estate transactions:
                </p>
                <ul className="contact-list">
                  <li>
                    <i className="fas fa-check-circle"></i>
                    <span>No commissions or real estate agent fees</span>
                  </li>
                  <li>
                    <i className="fas fa-check-circle"></i>
                    <span>No repairs or improvements needed</span>
                  </li>
                  {/* Item removed as requested */}
                  <li>
                    <i className="fas fa-check-circle"></i>
                    <span>No complicated paperwork - we handle it all</span>
                  </li>
                  {/* Item removed as requested */}
                </ul>
                <p className="mt-4">
                  Call us directly at <span className="highlight">(786) 460-6046</span> or fill out the form 
                  to start the process today!
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </FormSection>
  );
};

export default ContactForm;
